import * as React from 'react'
import { Col, Row } from 'reactstrap';
import { suspend } from 'suspend-react';
import { isMobileOnly } from '../controls/AccentDisplay';
import { EmailCellTemplate, MobileCellTemplate, WhatsAppCellTemplate } from '../controls/grids/GridColumnTemplates';
import { RTLGridCtrl } from '../controls/grids/RTLGridCtrl';
import { InLineFileNotes } from '../controls/tracking/TrackingNotes';
import { EmailDlg, mapToBaseEmailViewModel } from '../dialogs/EmailDlg';
import { SmsDlg } from '../dialogs/SmsDlg';
import { queryNoTracking, update, updateAsFormData } from '../services/DataService';
import { accentUtils, showDialog, t } from '../services/HelperService';
import { Helper } from '../services/JobService';
import GRIDS from './_GridDefinitions';
import { openCustomerChatDialog } from '../services/WhatsAppService';

const query_WebLeads_CustomFields = queryNoTracking("WebLeads_CustomFields");
const query_Templates = queryNoTracking("Templates");
const query_TemplatesByType = queryNoTracking("TemplatesByType");
const query_GridFilter_LeadSource = queryNoTracking("GridFilter_LeadSource");
const query_ExternalLeadNotes = queryNoTracking("ExternalLeadNotes", 0);

let customFields = null;

const getCustomFields = () => {

    if (accentUtils.isNull(customFields)) {
        return new Promise(p => {
            query_WebLeads_CustomFields.getAllWithCache().then(res => {
                customFields = res;
                p(customFields);
            });
        });
    } else {
        return Promise.resolve(customFields);
    }

};

export const ExternalLeadGrid = React.memo(props => {
    const additionalFields = isMobileOnly ? [] : suspend(async () => getCustomFields(), ["webLeadsCustomFields"]);

    const onSMSClick = e => {

        const leadData = e.row;

        query_Templates.getAllWithCache().then(t => {
            var templates = [];
            t.filter(a => a.Type === 'SMS' && a.Category === 'Web Lead' ).forEach(function (p) {
                templates.push({
                    ID: p.ID,
                    Body: p.Body,
                    Description: p.Description,
                    Subject: p.Subject
                })
            });

            var info = {
                ID: leadData.ID,
                ExternalLead: true,
                To: [{ Name: leadData.ContactFirstName + ' ' + leadData.ContactLastName, Mobile: leadData.ContactMobile }],
                Templates: templates,
                Configured: true
            };

            const ctx = {
                WebLeadID : leadData.ID
            };

            showDialog(<SmsDlg context={ ctx } info={info} />).then(r => {
                if (!r.canceled) {
                    Helper.sendWebLeadSms(
                        r.to.join(","),
                        r.Body,
                        leadData.ID,
                        r.DeliveryReceipt
                    ).then(res => {
                        props.methods.current.refresh();
                    });
                }
            });
        });
    };

    const onEmailClick = e => {

        var leadData = e.row;
        
        query_TemplatesByType.getAllWithCache({
            type: "Email",
            activity: false,
            job: false,
            opportunity: false,
            invoice: false,
            supply: false,
            statement: false,
            webLead: true
        }).then(t => {
            var templates = [];
            t.filter(a => a.Type === 'Email').forEach(function (p) {
                templates.push({
                    ID: p.ID,
                    Body: p.Body,
                    Description: p.Description,
                    Subject: p.Subject
                })
            });

            var info = {
                ID: leadData.ID,
                UploadID: Math.random().toString(16).substr(2, 10),
                To: [{ Name: leadData.ContactFirstName + ' ' + leadData.ContactLastName, Email: leadData.ContactEmail }],
                Templates: templates,
                Attachments: []
            };


            showDialog(<EmailDlg
                title="application_strings.views.settings.representative.emailLead"
                templates={info.Templates}
                to={info.To}
                attachments={info.Attachments}
                onSend={m => {
                    return new Promise(sp => {

                        const model = mapToBaseEmailViewModel(
                            m,
                            info.ID,
                            leadData.ContactFirstName + ' ' + leadData.ContactLastName,
                            {});

                        updateAsFormData("EmailWebLead", model).then(function (sr) {

                            if (sr.IsSent) {
                                sp({ error: null, msg: "application_strings.views.sendEmailDialog.sendEmailSuccess" });


                            } else {
                                sp({ error: { title: "application_strings.views.sendEmailDialog.titleResult", msg: sr.Response } });
                            }

                        });

                    });
                }}
            />).then(r => {
                if (!r.canceled)
                    props.methods.current.refresh();
            });

        });

    };


    const processColumn = col => {

        if (col.field === "LeadSource") {
            col.filterQuery = () => query_GridFilter_LeadSource.getAllWithCache();
        } else if (col.field === "ContactEmail") {
            col.cellTemplate = p => <EmailCellTemplate {...p} onClick={onEmailClick} />;
        } else if (col.field === "ContactMobile") {
            col.cellTemplate = p => (
                <div>
                    {p.dataItem[p.field]}
                    <div style={{ display: 'flex' }}>
                        <MobileCellTemplate {...p} onClick={onSMSClick} />
                        <WhatsAppCellTemplate {...p} onClick={() => openCustomerChatDialog(p.dataItem.ID, p.dataItem.ContactMobile)} />
                    </div>
                </div>
            );
        } else if (col.field === "ContactWorkPh" || col.field === "ContactHomePh") {
            col.cellTemplate = p => (
                <div>
                    {p.dataItem[p.field]}
                    <WhatsAppCellTemplate {...p} onClick={() => openCustomerChatDialog(p.dataItem.ID, p.dataItem[p.field])} />
                </div>
            );
        }

        return col
    };


    const cols = [...GRIDS.ExternalLeadGrid.Default, ...additionalFields.map(x => {
        return { fieldName: x.FieldName, caption: x.Name };
    })];



    return (
        <RTLGridCtrl
            methods={props.methods}
            query={s => props.query(s)}
            processColumn={processColumn}
            height={props.height}
            onRowClick={props.onRowClick}
            strings="externalLeadGrid"
            columns={cols}
            contextKey={GRIDS.ExternalLeadGrid.ContextKey}
            showToolbar={!props.hideToolbar}
            showSectionHeader={props.showSectionHeader}
            allowSearch
            onRefresh={ props.onRefresh}
            getActions={ props.getActions}
            gridID={props.gridID}
            detailsContext={{
                component: ExternalLeadGridDetailItem,
            }}
        />
    );
});


export const ExternalLeadGridDetailItem = React.memo(props => {


    const [fileNotes, setFileNotes] = React.useState(null);


    const loadFileNotes = React.useCallback((force) => {

        if (force) {
            query_ExternalLeadNotes.clearCache();
        }

        query_ExternalLeadNotes.getAllWithCache({ id: props.dataItem.ID })
            .then(n => {
                setFileNotes(n);
            });

    }, [fileNotes, setFileNotes]);

    const onNewFileNote = React.useCallback(notes => {
        update("NewFileNote", {
            Notes: notes,
            ExternalLeadID: props.dataItem.ID
        }).then(r => {
            loadFileNotes(true);
        });
    }, [props, loadFileNotes]);

    React.useEffect(() => {
        
        loadFileNotes();

    }, [loadFileNotes]);

    const leadData = accentUtils.isEmpty(props.dataItem.LeadData) ? null : JSON.parse(props.dataItem.LeadData);

    const hasData = !accentUtils.isEmpty(fileNotes) || !accentUtils.isNull(leadData);
    
    const fn = <InLineFileNotes notes={fileNotes} onNew={onNewFileNote} show />;

    const hasPOI = !accentUtils.isNull(leadData.ProductsOfInterest);

    const poi = hasPOI ? leadData.ProductsOfInterest.map(function (p) {
            return <Row>
                <Col md="9"><label>{p.Description}</label></Col>
                <Col md="3">{p.Qty}</Col>
            </Row>;
        }) : null;

    



    if (!hasData) return null;
    
    
    return <Row>
        <Col md={6}>
            <Row>
                <Col md="12" ><strong>{t("application_strings.views.leads.leadGrid.productsOfInterest")}</strong></Col>
            </Row>
            {poi}
        </Col>
        <Col md={6}>{fn}</Col>
    </Row>;
});
